import {
  GEO_TAX_SWITCH_ALREADY_ON_MESSAGE,
  GEO_TAX_SWITCH_NO_REAPPLY_MESSAGE,
  GEO_TAX_SWITCH_REAPPLY_FAILED_MESSAGE,
  GEO_TAX_SWITCH_REAPPLYING_MESSAGE,
  GEO_TAX_SWITCH_REASKING_MESSAGE,
} from '@revelio/core/global-ai-assistant/geo-taxonomy-switch';

import { applyGeographyTaxonomyPreference } from '../CustomDash/filters/utils/applyGeographyTaxonomyPreference';
import type { GlobalAssistantEntityType } from './global-ai-assistant-api';

/**
 * The trigger testid + switch copy live in `@revelio/core` (a leaf module with
 * no imports) rather than here: the Playwright suite has to assert on them, and
 * importing THIS module from a spec pulls `applyGeographyTaxonomyPreference` →
 * the `@revelio/core` barrel → `window` at load time, which kills collection.
 * Re-exported so app-side callers keep one import site.
 */
export {
  GEO_TAX_OFFER_TRIGGER_TESTID,
  GEO_TAX_SWITCH_ALREADY_ON_MESSAGE,
  GEO_TAX_SWITCH_NO_REAPPLY_MESSAGE,
  GEO_TAX_SWITCH_REAPPLY_FAILED_MESSAGE,
  GEO_TAX_SWITCH_REAPPLYING_MESSAGE,
  GEO_TAX_SWITCH_REASKING_MESSAGE,
} from '@revelio/core/global-ai-assistant/geo-taxonomy-switch';

/**
 * The target taxonomy of a geography-taxonomy substitution, read from a
 * `filter_response` artifact's root-level `geographyTaxonomyMismatch`.
 *
 * This is the ONLY sanctioned source for the id. The disclosure phrase the
 * model writes is free text and can name any taxonomy it likes; wiring a
 * preference write to it would let a hallucinated clause re-key the user's
 * whole account. Read defensively — the field is optional and older/cached
 * responses predate it entirely.
 */
export function readGeographyTaxonomyMismatchTargetId(
  payload: unknown
): number | undefined {
  if (typeof payload !== 'object' || payload === null) {
    return undefined;
  }
  const mismatch = (payload as { geographyTaxonomyMismatch?: unknown })
    .geographyTaxonomyMismatch;
  if (typeof mismatch !== 'object' || mismatch === null) {
    return undefined;
  }
  const { otherTaxonomyId, items } = mismatch as {
    otherTaxonomyId?: unknown;
    items?: unknown;
  };
  if (
    typeof otherTaxonomyId !== 'number' ||
    !Number.isInteger(otherTaxonomyId) ||
    otherTaxonomyId <= 0
  ) {
    return undefined;
  }
  // A mismatch with no substituted items describes nothing the user could
  // recognise, so there is nothing to disclose or switch for.
  if (!Array.isArray(items) || items.length === 0) {
    return undefined;
  }
  return otherTaxonomyId;
}

/**
 * Read the taxonomy id a narration stamped in `offerTargets.geo_tax`.
 * `offerTargets` values are message-id strings for every other kind, so the
 * numeric target rides as its decimal string. Anything unparseable resolves
 * to `undefined`, which downgrades the marker to plain text.
 */
/**
 * A chart whose breakdown level the user's geography view lacks ("pay by US
 * state" on Metro Area & City) carries `geoLevelSwitch` on its `chart_config`
 * artifact: the note render_chart hands the narration, and the view that has
 * the level. Validated like {@link readGeographyTaxonomyMismatchTargetId}: the
 * id comes from the server, never from the narration's own words.
 */
export function readChartGeoLevelSwitch(
  payload: unknown
): { note: string; otherTaxonomyId: number } | undefined {
  if (typeof payload !== 'object' || payload === null) return undefined;
  const value = (payload as { geoLevelSwitch?: unknown }).geoLevelSwitch;
  if (typeof value !== 'object' || value === null) return undefined;
  const { note, otherTaxonomyId } = value as {
    note?: unknown;
    otherTaxonomyId?: unknown;
  };
  if (
    typeof note !== 'string' ||
    note.trim() === '' ||
    typeof otherTaxonomyId !== 'number' ||
    !Number.isInteger(otherTaxonomyId) ||
    otherTaxonomyId <= 0
  ) {
    return undefined;
  }
  return { note, otherTaxonomyId };
}

export function parseGeoTaxOfferTargetId(
  stamped: string | undefined
): number | undefined {
  if (!stamped) {
    return undefined;
  }
  const parsed = Number(stamped);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

/** Exactly the shape the engine's `resolveThenEmitRouteFilters` is called with. */
export type GeographyTaxonomySwitchResolveArgs = {
  path: string;
  promptForFilters: string;
  entityType: GlobalAssistantEntityType;
  /**
   * The stored artifact for this prompt was resolved under the OUTGOING
   * taxonomy, so a cache hit would hand back exactly the response we are
   * trying to replace. (The cache key already partitions by `geo:` — this is
   * belt and braces for the same-tick window before the preference read
   * settles.)
   */
  bypassCache: true;
  /**
   * MERGE onto the page's applied filters, never replace them.
   *
   * The re-resolution runs only the ONE ask that earned this offer, which is
   * narrower than the filter state the conversation built up: "show engineers
   * in Hartford County" then "can you show me in the state of connecticut"
   * leaves Engineering applied, but the second ask alone resolves to a
   * geography and nothing else. Replacing would drop the role — and WHICH
   * filters vanished would depend on which of the two offers you clicked,
   * since each carries its own prompt (John, 2026-09-01).
   *
   * A taxonomy switch is a RE-KEYING of geography, so geography is the only
   * dimension it may change. Merging is what makes that true: the resolved
   * geography overwrites the stale one (both taxonomies use the same
   * dimension-level `GEOGRAPHY`/`MULTI_GEOGRAPHY` ids, so the outgoing
   * selections cannot survive alongside the incoming ones) while every other
   * dimension the user established is carried over untouched.
   */
  merge: true;
  /**
   * The taxonomy the fresh request must run under, passed EXPLICITLY rather
   * than read back from the preference: the write above lands in session
   * storage and reaches the taxonomy hook a render later, so a read here
   * could still return the outgoing id.
   */
  geoTax: number;
};

/**
 * The inline geography-taxonomy switch, end to end.
 *
 * RE-RESOLVE, never replay. `useApplyAiSearchFilters` DROPS a response's
 * geography when its `geo_tax_id` differs from the provider's active taxonomy
 * — so replaying the stored (outgoing-taxonomy) artifact after the switch
 * lands the page unfiltered behind a console warning. The only correct move
 * is a fresh resolution carrying the NEW `geoTax`, which is what
 * `resolveThenEmitRouteFilters` performs before emitting its apply command.
 *
 * Deps are injected so the whole flow — both preference writes AND the
 * re-resolution contract — is assertable without mounting the engine.
 */
export async function runGeographyTaxonomySwitch(params: {
  /** From the turn's `filter_response` artifact. Never from model text. */
  nextTaxonomyId: number;
  /**
   * The taxonomy the account is on NOW. When it already equals
   * `nextTaxonomyId` the offer has been taken (the trigger stays clickable in
   * the transcript), so the click must not write the preference again or
   * re-ask a second time. Omitted → no such guard (older call sites).
   */
  currentTaxonomyId?: number;
  path: string;
  entityType: GlobalAssistantEntityType;
  /** The prompt to re-resolve; absent/blank → preference-only switch. */
  promptForFilters?: string | null;
  /** False on surfaces with no filterable page (e.g. the assistant homepage). */
  canApplyFiltersOnCurrentPage: boolean;
  resolveThenEmitRouteFilters: (
    args: GeographyTaxonomySwitchResolveArgs
  ) => Promise<boolean>;
  notify: (message: { text: string; kind: 'text' | 'error' }) => void;
  /**
   * Start a fresh assistant turn for `prompt`. Used where there is no page to
   * re-key in place: a data answer asked from the homepage disclosed the
   * substitution and offered the switch, and the click then dead-ended on
   * "open a page with filters and ask again" (Danny, 2026-09-03). Re-asking
   * the same question under the new view re-resolves it AND lands the page
   * link the answer carries — the geography the user wanted, one click away.
   * Optional so older call sites keep the preference-only behaviour.
   */
  reask?: (prompt: string) => void;
  /**
   * The offer came from a chart, a preview with its own filters: re-ask so
   * the chart is drawn again under the new view, and never apply its ask to
   * the page the user happens to be on.
   */
  reaskOnly?: boolean;
  /** Seam for tests; defaults to the shared two-part preference write. */
  applyPreference?: (nextTaxonomyId: number) => void;
}): Promise<void> {
  const applyPreference =
    params.applyPreference ?? applyGeographyTaxonomyPreference;
  if (params.currentTaxonomyId === params.nextTaxonomyId) {
    params.notify({ text: GEO_TAX_SWITCH_ALREADY_ON_MESSAGE, kind: 'text' });
    return;
  }
  // The preference write is the user's consent taking effect; it happens even
  // when there is nothing to re-apply here (their other pages re-key too).
  applyPreference(params.nextTaxonomyId);

  const promptForFilters = params.promptForFilters?.trim();
  if (!promptForFilters) {
    params.notify({ text: GEO_TAX_SWITCH_NO_REAPPLY_MESSAGE, kind: 'text' });
    return;
  }
  if (!params.canApplyFiltersOnCurrentPage || params.reaskOnly) {
    if (!params.reask) {
      params.notify({ text: GEO_TAX_SWITCH_NO_REAPPLY_MESSAGE, kind: 'text' });
      return;
    }
    params.notify({ text: GEO_TAX_SWITCH_REASKING_MESSAGE, kind: 'text' });
    params.reask(promptForFilters);
    return;
  }

  params.notify({ text: GEO_TAX_SWITCH_REAPPLYING_MESSAGE, kind: 'text' });
  const resolved = await params.resolveThenEmitRouteFilters({
    path: params.path,
    promptForFilters,
    entityType: params.entityType,
    bypassCache: true,
    // See `merge` on GeographyTaxonomySwitchResolveArgs: re-keying geography
    // must not drop the filters earlier turns established.
    merge: true,
    geoTax: params.nextTaxonomyId,
  });
  if (!resolved) {
    params.notify({
      text: GEO_TAX_SWITCH_REAPPLY_FAILED_MESSAGE,
      kind: 'error',
    });
  }
}
